<template>
  <VDialogV3
    ref="dialog"
    id="demand-v2-card"
    position="right"

    :entity="localDemandV2"
    :feed-entity-pointer="demandV2Pointer"

    :tabs="dialogTabs"
    v-model:active-tab="activeTab"
    :loading="localDemandV2 === null"

    :context-menu="dialogContextMenu"
    :hide-footer="activeTab.id !== 'main'"
  >
    <template #badges>
      <VBadge v-if="isApproved" class="primary">Утверждено</VBadge>
    </template>

    <!-- ===== Таб: Основное ===== -->
    <template v-if="activeTab.id === 'main'">

      <!-- Режим создания -->
      <section v-if="isCreateOriginalMode">
        <VSuggestionSelectV2
          v-model="localDemandV2.product"
          :data="(({ pattern }) => ProductsService.search(pattern))"
          label="Наименование"
          layout="vertical"
          required
          :disabled="isAddNewRequestMode"
          @input="onProductInput"
        >
          <template #helper-text>
            Артикул:
            <template v-if="localDemandV2.buyersCode">
              <VLink @click="openCategoryPopup">
                {{ localDemandV2.buyersCode }}
              </VLink>
            </template>
            <template v-else>
              отсутствует
            </template>
          </template>
        </VSuggestionSelectV2>

        <VSwitch
          v-if="hasPossibleDemands"
          v-model="isAddNewRequestMode"
          theme="slider"
          unchecked-label="Создать новую потребность"
          checked-label="Обогатить имеющуюся потребность"
          @update:modelValue="onIsAddNewRequestMode"
        />

        <VFormItem
          layout="vertical"
        >
          <VInputNumberRusV2
            v-model="localDemandV2.requests[0].qtyDesired"
            label="Количество"
            layout="vertical"
            required
            :max="1000000000000000"
            :min="0.001"
            :type="Qty"
          />
          <VSelectV2
            v-model="localDemandV2.unit"
            required
            label="Ед. изм"
            layout="vertical"
            :data="$core.dictionaries.units"
          />
        </VFormItem>

        <VDateInput
          v-model="localDemandV2.requests[0].desiredReceiptDate"
          label="Желаемая дата доставки"
          layout="vertical"
          required
          :validate-method="validateDesiredReceiptDate"
        />

        <!-- При создании потребности это поле важно поднять повыше -->
        <VSelectV2
          v-model="localDemandV2.buyerProductionSite"
          label="Производственная площадка"
          :clearable="false"
          layout="vertical"
          required
          :data="productionSiteSource"
          :disabled="isAddNewRequestMode"
        />
      </section>

      <!-- Режим редактирования -->
      <section v-if="isEditOriginalMode">
        <VInput
          v-model="localDemandV2.name"
          label="Наименование"
          layout="vertical"
        >
          <template #helper-text>
            <span>Артикул:</span>
            <template v-if="localDemandV2.buyersCode">
              <VLink
                @click="openCategoryPopup"
              >
                {{ localDemandV2.buyersCode }}
              </VLink>
            </template>
            <template v-else>
              --
            </template>
          </template>
        </VInput>

        <VSelectV2
          v-model="localDemandV2.buyerProductionSite"
          label="Производственная площадка"
          :clearable="false"
          required
          layout="vertical"
          :data="productionSiteSource"
        />

        <VSwitch
          v-model="localDemandV2.isApproved"
          :disabled="isLoading || !isOptionsSelected"
          layout="vertical"
          theme="slider"
          label="Проверка бюджета"
          checked-label="Утверждён"
          unchecked-label="Не утверждён"
          @update:modelValue="onIsApprovedInput"
        />
      </section>

      <!-- Комментарий (только создание) -->
      <template v-if="isCreateOriginalMode">
        <hr>
        <section>
          <VTextarea
            v-model="creationComment"
            label="Комментарий"
            layout="vertical"
          />
        </section>
      </template>

      <!-- Заявки / варианты / прошлые сделки (только редактирование) -->
      <template v-if="isEditOriginalMode">
        <hr>

        <DemandV2RequestsOptionSection
          :demand-v2="localDemandV2"
          @DemandRequest:created="renewLocalDemandV2"
          @DemandRequest:updated="renewLocalDemandV2"
          @DemandRequest:deleted="renewLocalDemandV2"
        />

        <hr>

        <DemandV2OptionsSection
          :demand-v2="localDemandV2"
          @DemandOption:created="renewLocalDemandV2"
          @DemandOption:updated="renewLocalDemandV2"
          @DemandOption:deleted="renewLocalDemandV2"
        />

        <hr>

        <DemandV2PrevDealsSection
          :demand-v2="localDemandV2"
        />

        <hr>
      </template>

      <!-- Номер заявки -->
      <section>
        <VInput
          v-model="localDemandV2.requestNumber"
          label="Номер заявки"
          layout="vertical"
          :disabled="isAddNewRequestMode"
        >
          <template #helper-text>
            Если у вас есть номер заявки из которой вы добавляете потребность, укажите его.
          </template>
        </VInput>
      </section>

      <hr>

      <!-- Покупатель -->
      <section>
        <VSelectV2
          v-model="localDemandV2.buyer"
          label="Покупатель"
          layout="vertical"
          :clearable="false"
          disabled
        />

        <VSelectV2
          v-model="localDemandV2.buyerCompany"
          label="Юр лицо"
          layout="vertical"
          :clearable="false"
          :data="buyerCompanySource"
          :disabled="isAddNewRequestMode"
        />

        <VSelectV2
          v-model="localDemandV2.buyerStore"
          label="Склад"
          required
          :clearable="false"
          layout="vertical"
          :data="buyerStoreSource"
          :disabled="isAddNewRequestMode"
        />

        <VSelectV2
          v-model="localDemandV2.responsibleBuyer"
          label="Ответственный"
          required
          layout="vertical"
          :clearable="false"
          :data="responsibleSource"
          :disabled="isAddNewRequestMode"
        />
      </section>

      <!-- Категория заказа (только создание) -->
      <template v-if="!isEditOriginalMode">
        <hr>
        <section>
          <VSelectV2
            v-model="localDemandV2.orderCategory"
            label="Категория заказа"
            layout="vertical"
            disabled
          />
        </section>
      </template>
    </template>

    <!-- ===== Таб: Файлы =====
         v-else-if, а не отдельный v-if: три независимых v-if компилируются
         в три блока со своими якорями, и при переключении таба Vue гасит
         фрагмент main и вставляет соседний блок относительно уже уехавшего
         якоря — отсюда insertBefore: Child to insert before is not a child
         of this node. Ветки и так взаимоисключающие (различаются по
         activeTab.id), поведение цепочка не меняет. -->
    <FilesList
      v-else-if="isEditOriginalMode && activeTab.id === 'files'"
      :entities="fileEntities"
    >
      <template #group-hint="{ entity }">
        <dl v-if="entity.typeName === 'demandOption' && fileGroupHints.has(entity.id)" class="group-header-description">
          <dt class="description"> Поставщик: </dt> <dd class="description"> {{ fileGroupHints.get(entity.id).seller }} </dd>
          <dt class="description"> Цена поставщика:</dt> <dd class="description"> {{ fileGroupHints.get(entity.id).priceSeller }} </dd>
        </dl>
      </template>
    </FilesList>

    <!-- ===== Таб: Прочее ===== -->
    <section v-else-if="isEditOriginalMode && activeTab.id === 'misc'">
      <VSelectV2
        v-model="localDemandV2.orderCategory"
        label="Категория заказа"
        layout="vertical"
        disabled
      />
    </section>

    <!-- ===== Footer =====
         Слот отдаём всегда, видимостью управляет :hide-footer по активному
         табу. Прежний v-if="isEditOriginalMode" делал обратное задуманному:
         на табах «Файлы» и «Прочее» футер оставался, а в режиме создания
         слота не было вовсе — диалог подставлял свой дефолтный футер, чья
         кнопка эмитит submit, который здесь никто не слушает. То есть
         создать потребность из карточки было нельзя: кнопка «Создать» ниже
         лежит в этом слоте и до экрана не доходила.

         Кнопок здесь больше двух и они условные, поэтому дефолтный футер
         диалога не подходит. Плата за это — гейт валидации не отрабатывает
         сам: сохраняющие обработчики (createDemand, updateDemand) зовут
         $refs.dialog.validateAndFocus() первой строкой. -->
    <template #footer>
      <div class="actions">
        <VButton
          v-if="!editMode || isAddNewRequestMode"
          theme="primary"
          :disabled="isLoading"
          @click="createDemand"
        >
          Создать
        </VButton>

        <VButton
          v-if="editMode && !isAddNewRequestMode"
          theme="primary"
          :disabled="isLoading"
          @click="updateDemand"
        >
          Сохранить
        </VButton>

        <template v-if="isEditOriginalMode">
          <VButton
            v-if="localDemandV2.isWaitingForOptions"
            theme="default"
            :disabled="sendDemandV2ForwardDisabled"
            @click="sendDemandV2ToChooseOptions"
          >
            Отправить на выбор варианта
          </VButton>

          <VButton
            v-if="localDemandV2.isWaitingForChoose"
            theme="default"
            :disabled="sendDemandV2ForwardDisabled"
            @click="sendDemandV2ToApproveOptions"
          >
            Отправить на проверку
          </VButton>
        </template>

        <VButton
          v-if="isOptionsSelected"
          theme="default"
          :disabled="isLoading"
          @click="showMoveDemandDialog"
        >
          Вернуть на доработку
        </VButton>
      </div>

      <VButton @click="$core.dialogs.DemandV2Card.hide()">
        Закрыть
      </VButton>
    </template>

    <!-- Поп-ап категории по артикулу -->
    <VPopup
      v-if="localDemandV2?.productCategory"
      :visible="categoryPopup.visible"
      :target="categoryPopup.target"
      :position="categoryPopup.position"
      padding="5px"
      show-pointer
      @close="closeCategoryPopup"
    >
      Наименование: {{ localDemandV2?.productCategory.name }}
    </VPopup>
  </VDialogV3>
</template>

<style lang="scss" src="./DemandV2Card.scss"/>

<script lang="ts">

import EntityPointer                 from '@/domains/EntityPointer/EntityPointer';
import DemandV2                      from '@/domains/DemandsV2/DemandV2';
import PersonsService                from '@/domains/Persons/PersonsService';
import DemandsV2Service              from '@/domains/DemandsV2/DemandsV2Service';
import ProductionSitesService        from '@/domains/ProductionSites/ProductionSitesService';
import CompaniesService              from '@/domains/Companies/CompaniesService';
import StoresService                 from '@/domains/Stores/StoresService';
import type Partner                  from '@/domains/Partners/Partner';
import type Company                  from '@/domains/Companies/Company';
import type Store                    from '@/domains/Stores/Store';
import ContextMenuItem               from '@/lib/components/VContextMenu/ContextMenuItem';
import Comment                       from '@/core/services/Comments/Comment';
import ProductsService               from "@/domains/Products/ProductsService";
import Qty                           from "@/core/types/Qty";
import DateSimple                    from "@/core/types/DateSimple";

import DemandV2OptionsSection        from '@/domains/DemandsV2/components/DemandV2Card/DemandV2OptionsSection.vue';
import DemandV2RequestsOptionSection from '@/domains/DemandsV2/components/DemandV2Card/DemandV2RequestsOptionSection.vue';
import DemandV2PrevDealsSection      from '@/domains/DemandsV2/components/DemandV2Card/DemandV2PrevDealsSection.vue';
import FilesList                     from '@/core/services/Files/components/FilesList/FilesList.vue';

export default defineComponent({
  name: 'DemandV2Card',

  components: {
    DemandV2RequestsOptionSection,
    DemandV2OptionsSection,
    DemandV2PrevDealsSection,
    FilesList,
  },

  props: {
    id: {
      type:    Number,
      default: null,
    },
  },

  setup() {
    return {
      Qty,
      ProductsService,
    }
  },

  emits: [
    'close',
    DemandV2.Events.Created,
    DemandV2.Events.Updated,
    DemandV2.Events.Deleted,
  ],

  data() {
    /**
     * Правило прелоада зависимых источников документа:
     * - новый — preloadOnMount: опции готовы до первого клика,
     *   зависимые подтянутся сами при выборе родителя;
     * - существующий — preloadOnFocus: шанс, что реквизиты станут
     *   менять, низок — впустую не грузим.
     *
     * Признак берём из пропа id, а не из localDemandV2: data() отрабатывает
     * до init(), сущности в этот момент ещё нет, и условие на ней всегда
     * сводилось бы к «создание».
     */
    const isNewDocument = this.$core.utils.isNullOrUndefined(this.id);

    return {
      DemandV2,
      DemandsV2Service,
      PersonsService,

      localDemandV2: null as DemandV2 | null,

      creationComment: '' as string,

      /**
       * Режим, зафиксированный на момент открытия карточки.
       * Нужен, чтобы переключение «обогатить потребность» по ходу работы не перетасовывало
       * поля формы — раскладка остаётся той, с которой пользователь зашёл.
       *
       * Считается из пропа: он и есть исходное состояние, менять его по ходу
       * жизни диалога некому. Прежде поле заполнялось в init() из
       * localDemandV2.id — то же значение, только на полтакта позже.
       */
      isEditOriginalMode: isNewDocument === false,

      activeTab: {id: 'main', name: 'Основное'},
      isAddNewRequestMode: false,

      categoryPopup: {
        target:   null,
        visible:  false,
        position: null,
      },

      possibleDemands: [],

      isLoading: false,

      /** Ответственный ищется по паттерну в пределах партнёра-покупателя.
       *  buyer читается лениво: на момент создания источника потребность
       *  ещё не загружена (init отрабатывает в mounted). */
      responsibleSource: this.$core.loader.select(({ pattern }) => PersonsService.searchPersons(pattern as string, this.localDemandV2!.buyer.id)),

      /**
       * Юрлица покупателя грузятся запросом по партнёру, а не берутся из
       * buyer.companies: потребность приходит с сервера с усечённым
       * партнёром, и коллекции у него может не быть — селект молча оказался
       * бы пустым.
       *
       * buyer читается лениво: на момент создания источника потребность ещё
       * не загружена (init отрабатывает в mounted).
       */
      buyerCompanySource: this.$core.loader.select(({ buyer }) => this.loadBuyerCompanies(buyer))
        .dependsOn({ buyer: () => this.localDemandV2?.buyer })
        .preloadOnFocus(),

      /** Склады покупателя — по тем же основаниям, что и юрлица. */
      buyerStoreSource: this.$core.loader.select(({ buyer }) => this.loadBuyerStores(buyer))
        .dependsOn({ buyer: () => this.localDemandV2?.buyer })
        .preloadOnFocus(),

      /**
       * Площадки принадлежат юрлицу покупателя, а оно в карточке
       * редактируемое — поэтому dependsOn, а не разовая догрузка в init:
       * при смене юрлица список перезагрузится сам. Прежняя схема грузила
       * площадки один раз для исходного юрлица, и после его смены селект
       * молча пустел.
       *
       * Режим — по пропу id: data() отрабатывает до init(), и на
       * localDemandV2 в этот момент опираться нельзя.
       */
      productionSiteSource: this.$core.loader.select(({ buyerCompany }) => ProductionSitesService.getList(null, buyerCompany.id))
        .dependsOn({ buyerCompany: () => this.localDemandV2?.buyerCompany })
        .preloadOnMount(isNewDocument)
        .preloadOnFocus(isNewDocument === false),
    };
  },

  async mounted(): Promise<void> {
    await this.init();
  },

  computed: {
    /** Указатель на потребность для фида. */
    demandV2Pointer(): EntityPointer | null {
      if (this.localDemandV2?.id) {
        return new EntityPointer('demandV2', this.localDemandV2.id);
      }
      else {
        return null;
      }
    },

    /**
     * Табы карточки (основное/файлы/прочее). Состав зависит от роли, наличие —
     * от пропа id, а не от localDemandV2.isExisting.
     *
     * Смысл тот же — «табы только у сохранённой», — но момент вычисления
     * другой: isExisting доступен лишь после загрузки, и табы появлялись в тот
     * же тик, когда слот монтируется впервые. VDialogV3 на появление табов
     * синхронно зовёт onTabClick и пишет в activeTab прямо во время патча
     * родителя. isEditOriginalMode известен из пропа, до mounted — табы есть
     * с первого рендера и не мигают. Тот же признак у веток содержимого
     * табов ниже, так что панель и содержимое не разъезжаются.
     */
    dialogTabs(): { id: string; name: string }[] | null {
      if (this.isEditOriginalMode) {
        return DemandsV2Service.getTabs();
      }
      else {
        return null;
      }
    },

    /** Меню тулбара — по тому же признаку и по той же причине. */
    dialogContextMenu(): ContextMenuItem<DemandV2>[] | null {
      if (this.isEditOriginalMode) {
        return DemandsV2Service.contextMenu;
      }
      else {
        return null;
      }
    },

    fileEntities(): EntityPointer[] {
      if (!this.localDemandV2) {
        return [];
      }

      return [
        new EntityPointer('demandV2', this.localDemandV2.id, 'Потребность ' + this.localDemandV2.name),
      ].concat(
          this.localDemandV2.options.map((option) =>
              new EntityPointer(
                  'demandOption',
                  option.id,
                  'Вариант ' + (this.localDemandV2.name !== option.name ? option.name ?? '' : '')
              )
          )
      );
    },

    /**
     * Подсказки для групп файлов по вариантам: поставщик и его цена уже в
     * готовом к выводу виде, ключ — id варианта.
     *
     * Слот #group-hint вызывался на каждую группу и на каждый ре-рендер, а
     * внутри дважды искал вариант линейным find по options. Здесь тот же
     * поиск делается один раз на построение карты, а форматирование цены
     * уходит из разметки туда, где виден источник кода валюты.
     */
    fileGroupHints(): Map<number, { seller: string, priceSeller: string }> {
      const result = new Map<number, { seller: string, priceSeller: string }>();

      if (!this.localDemandV2) {
        return result;
      }

      for (const option of this.localDemandV2.options) {
        let sellerTitle: string;

        /** Поставщика в варианте может ещё не быть — заводят его прямо здесь. */
        if (option.seller) {
          sellerTitle = option.seller.toString();
        }
        else {
          sellerTitle = '';
        }

        result.set(option.id, {
          seller:      sellerTitle,
          priceSeller: option.priceSeller.format(option.offerCurrencyCode, option.unit),
        });
      }

      return result;
    },

    isOptionsSelected(): boolean {
      return Boolean(this.localDemandV2?.isOptionsSelected);
    },

    isApproved(): boolean {
      return Boolean(this.localDemandV2?.isApproved);
    },

    isCreateOriginalMode(): boolean {
      return !this.isEditOriginalMode;
    },

    editMode(): boolean {
      return Boolean(this.localDemandV2?.isExisting);
    },

    /** Можем ли мы предложить пользователю обогатить имеющуюся сущность? */
    hasPossibleDemands(): boolean {
      return this.$core.utils.isNotEmptyArray(this.possibleDemands);
    },

    /** f/6405 смена label, disabled, title в зависимости от таба */
    sendDemandV2ForwardDisabled(): boolean {
      if (!this.localDemandV2) {
        return true;
      }
      if (this.localDemandV2.hasOptions === false) {
        return true;
      }
      if (this.isLoading) {
        return true;
      }

      // Если уже были выбраны — даём возможность "отправить на согласование"
      return this.localDemandV2.isOptionsExists && this.localDemandV2.hasChosenOptions === false;
    },
  },

  methods: {
    /** Юрлица покупателя. Без выбранного партнёра грузить нечего. */
    loadBuyerCompanies(buyer: Partner): Promise<Company[]> | Company[] {
      if (this.$core.utils.isNullOrUndefined(buyer?.id)) {
        return [];
      }
      else {
        return CompaniesService.getList({ partnerId: buyer.id });
      }
    },

    /** Склады покупателя. Условие то же, что у юрлиц. */
    loadBuyerStores(buyer: Partner): Promise<Store[]> | Store[] {
      if (this.$core.utils.isNullOrUndefined(buyer?.id)) {
        return [];
      }
      else {
        return StoresService.getList({ partnerId: buyer.id });
      }
    },

    /**
     * Загрузка по эталону AssemblyCard: всегда грузим потребность с бэка если пришел id,
     * в ином случае создаем пустую
     */
    async init(): Promise<void> {
      if (this.id !== null) {
        this.localDemandV2 = await DemandsV2Service.get(this.id!);
      }
      else {
        this.localDemandV2 = DemandV2.fromNew();
      }
    },

    async onIsApprovedInput(value) {
      this.freezeActions();

      if (value === true) {
        this.localDemandV2.approve();
      }
      else {
        this.localDemandV2.reject();
      }

      const isSuccess = await DemandsV2Service.update(this.localDemandV2);
      if (!isSuccess) {
        return;
      }

      await this.renewLocalDemandV2();
      this.$emit(DemandV2.Events.Updated);

      this.unfreezeActions();
      // Нужно ли закрывать диалог? я не уверен. Если пользователь ошибся?
      // Диалог закроется, реестр обновиться, потребность уже не найти если список огромный
      // так что пускай останется открытым
      // this.closeDialog();
    },

    async createDemand() {
      // Кнопка лежит в слоте #footer, дефолтный сабмит VDialogV3 не отрабатывает,
      // поэтому гейт валидации зовём руками. Без него невалидная форма уходила
      // на бек и падала там фаталом на неинициализированных полях.
      if (await this.$refs.dialog.validateAndFocus() === false) {
        return;
      }

      if (this.isAddNewRequestMode) {
        await this.attachToDemand();
      }
      else {
        await this.createNewDemand();
      }
    },

    async createNewDemand() {
      this.freezeActions();

      // Комментарий при создании — оборачиваем в Comment и кладём в буфер commentaries.
      // Бек развернёт его в реальный коммент через CommentsService::addComments вместе
      // с сохранением потребности. id у EntityPointer тут null (сущность ещё не создана) —
      // бек привяжет коммент к свежесозданной потребности.
      if (this.$core.utils.string.isNotEmptyString(this.creationComment)) {
        const comment = Comment.fromNew(
          new EntityPointer('demandV2', this.localDemandV2.id, 'Потребность'),
          this.creationComment,
        );
        //comment.createdBy = this.$core.currentUser;
        //comment.createdAt = new TimeStamp();

        this.localDemandV2.commentaries = [comment];
      }

      const isSuccess = await DemandsV2Service.add(this.localDemandV2);
      this.unfreezeActions();

      if (!isSuccess) {
        return;
      }

      this.$emit(DemandV2.Events.Created);
      this.$core.dialogs.DemandV2Card.resolve();
    },

    async attachToDemand() {
      this.freezeActions();
      const isSuccess = await DemandsV2Service.addRequest(this.localDemandV2.requests[0]);
      this.unfreezeActions();

      if (!isSuccess) {
        return;
      }

      this.$emit(DemandV2.Events.Updated);
      this.$core.dialogs.DemandV2Card.resolve();

      /** Если мы только что прикрепили заявку, открываем карточку */
      void DemandsV2Service.showDemandV2Card(this.localDemandV2);
    },

    async updateDemand() {
      // Кнопка лежит в слоте #footer, дефолтный сабмит VDialogV3 не отрабатывает,
      // поэтому гейт валидации зовём руками.
      if (await this.$refs.dialog.validateAndFocus() === false) {
        return;
      }

      this.freezeActions();
      /** Обновление потребности на самом деле состоит из нескольких кусочков */
      /** Возможно пользователь обновил реквест, писать что-то что достоверно сказало бы об этом нет времени */
      await Promise.all([
        this.updateRequests(this.localDemandV2),
        DemandsV2Service.update(this.localDemandV2),
      ]);

      await this.renewLocalDemandV2();

      this.unfreezeActions();
    },

    async renewLocalDemandV2() {
      this.$refs.dialog?.renewFeed();
      this.localDemandV2 = await DemandsV2Service.get(this.localDemandV2.id);
      this.$emit(DemandV2.Events.Updated);
    },

    updateRequests(demand) {
      return Promise.all(
        demand.requests.map(_request => DemandsV2Service.updateRequest(_request)),
      );
    },

    showMoveDemandDialog() {
      DemandsV2Service.showMoveDemandDialog(
        this.localDemandV2,
        () => {
          this.$emit(DemandV2.Events.Updated);
          this.$core.dialogs.DemandV2Card.resolve();
        },
      );
    },

    /** Если я хочу обогатить имеющуюся потребность,
     * то я присваиваю текущей модели состояние из какой-то уже полученной */
    async onIsAddNewRequestMode(value) {
      if (value === true) {
        const backendVersion = await DemandsV2Service.get(this.possibleDemands[0].id);
        this.localDemandV2   = DemandV2.enrich(backendVersion);
      }
      else {
        /** А если я ошибся, и я случайно кликнул, теперь перевёл свитчер в false */
        this.localDemandV2 = DemandV2.prune(this.localDemandV2);
      }
    },

    async sendToChooseOptionsStage() {
      this.localDemandV2.moveToChooseOptionsStage();

      const isSuccess = await DemandsV2Service.update(this.localDemandV2);
      if (isSuccess) {
        //todo уведомление это сервисный уровень
        this.$core.showNotification.success('Потребность отправлена к выбору вариантов');

        this.$emit(DemandV2.Events.Updated);
      }
    },

    async sendToApproveOptionsStage() {
      this.localDemandV2.moveToApproveOptionsStage();

      const isSuccess = await DemandsV2Service.update(this.localDemandV2);
      if (isSuccess) {
        //todo уведомление это сервисный уровень
        this.$core.showNotification.success('Потребность отправлена на утверждение');

        this.$emit(DemandV2.Events.Updated);
      }
    },

    freezeActions() {
      this.isLoading = true;
    },

    unfreezeActions() {
      this.isLoading = false;
    },

    async sendDemandV2ToChooseOptions() {
      this.freezeActions();

      await this.sendToChooseOptionsStage();

      this.unfreezeActions();
      this.$core.dialogs.DemandV2Card.resolve();
    },

    async sendDemandV2ToApproveOptions() {
      this.freezeActions();

      await this.sendToApproveOptionsStage();

      this.unfreezeActions();
      this.$core.dialogs.DemandV2Card.resolve();
    },

    async onProductInput(value) {
      if (this.$core.utils.number.isNumber(value.id)) {
        this.possibleDemands = await DemandsV2Service.getList({ product: value });
      }
    },

    openCategoryPopup(event) {
      this.categoryPopup.target  = event.currentTarget;
      this.categoryPopup.visible = true;
    },

    closeCategoryPopup() {
      this.categoryPopup.visible = false;
    },

    /**
     * Дата доставки должна быть будущей.
     *
     * Метод зовётся не только на введённое значение: VDateInput через него
     * решает, гасить ли день в календарной сетке, и передаёт туда нативные
     * Date по одному на каждую ячейку месяца. Поэтому вход приводим к
     * DateSimple сами — статический DateSimple.toDayStart принимает только
     * DateSimple и на нативной дате бросает TypeError.
     *
     * Сравнение идёт через isAfter, он принимает и Date, и DateSimple.
     * today() уже нормализован к началу дня, так что дата ровно сегодняшняя
     * даёт false и отбраковывается — как и было задумано.
     */
    validateDesiredReceiptDate(value: DateSimple | Date | null): string | null {
      if (value === null || value === undefined) {
        return null;
      }
      else {
        const date = new DateSimple(value);

        if (date.isAfter(DateSimple.today())) {
          return null;
        }
        else {
          return 'Нельзя указать прошедшую дату';
        }
      }
    },
  },
});
</script>